import { create } from 'zustand';
import { listScenarios, updateProject } from '../api/client';
import { computePixelsPerMeter } from '../lib/calibration';
import { effectiveGridSize } from '../lib/grid';
import {
  defaultNodeName,
  snapValue,
  withTypeNodeColors,
} from '../lib/nodeConfig';
import { resolveNodeTypeAppearance } from '../lib/nodeVisualGeometry';
import {
  addNodesToGroup,
  collectGroupMemberIds,
  findGroupById,
  normalizeNodeGroups,
  pruneStaleNodeGroupReferences,
  removeNodesFromAllGroups,
  syncNodeGroupsToDocument,
  withPrunedNodeGroups,
} from '../lib/nodeGroups';
import { typeSpecificDefaults } from '../lib/nodeDefaults';
import { normalizeProjectDocument } from '../lib/projectNormalization';
import { syncRouteLengthsInDocument } from '../lib/routeGeometry';
import type {
  CalibrationData,
  CalibrationPoint,
  NodeGroup,
  NodeType,
  ProjectDocument,
  ProjectNode,
  ProjectRecord,
  ProjectRoute,
  ProjectSettings,
  UnitSystem,
  ProjectPermissionLevel,
} from '../types/project';
import { useSimulationStore } from './simulationStore';
import {
  cloneDocument,
  pushDocumentSnapshot,
  trimHistoryStack,
} from '../lib/editorHistory';

// ------------------------------------------------------------
// Editor state (Zustand)
// ------------------------------------------------------------

interface HistoryOptions {
  recordHistory?: boolean;
}

interface LoadProjectOptions {
  preserveUi?: boolean;
}

export interface SelectOptions {
  focus?: boolean;
  additive?: boolean;
  subtractive?: boolean;
}

interface EditorState {
  projectId: string | null;
  record: ProjectRecord | null;
  document: ProjectDocument | null;
  selectedNodeIds: string[];
  selectedRouteIds: string[];
  /** When set, the canvas is in drill-down mode for editing one group. */
  focusedGroupId: string | null;
  selectionFocusSeq: number;
  nodeClipboard: ProjectNode[] | null;
  editingNodeId: string | null;
  isDirty: boolean;
  isSaving: boolean;
  isLoading: boolean;
  error: string | null;
  snapToGrid: boolean;
  showGrid: boolean;
  routeSourceId: string | null;
  calibrationMode: boolean;
  calibrationPoints: [CalibrationPoint | null, CalibrationPoint | null];
  calibrationDistanceInput: string;
  permission: ProjectPermissionLevel;
  historyPast: ProjectDocument[];
  historyFuture: ProjectDocument[];

  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  loadProject: (record: ProjectRecord, options?: LoadProjectOptions) => void;
  setDocument: (document: ProjectDocument) => void;
  selectNode: (id: string | null, options?: SelectOptions) => void;
  setNodeSelection: (ids: string[], options?: SelectOptions) => void;
  setMarqueeSelection: (
    nodeIds: string[],
    routeIds: string[],
    options?: SelectOptions,
  ) => void;
  selectRoute: (id: string | null, options?: SelectOptions) => void;
  focusSelection: () => void;
  openNodeEdit: (id: string) => void;
  closeNodeEdit: () => void;
  removeNodeFromDocument: (id: string) => void;
  setSaving: (saving: boolean) => void;
  markSaved: () => void;
  saveProject: () => Promise<boolean>;
  reset: () => void;
  toggleSnapToGrid: () => void;
  toggleShowGrid: () => void;
  setRouteSource: (id: string | null) => void;
  addNode: (type: NodeType) => void;
  updateNode: (id: string, patch: Partial<ProjectNode>) => void;
  moveNode: (id: string, x: number, y: number, options?: HistoryOptions) => void;
  moveSelectedNodesByDelta: (
    dx: number,
    dy: number,
    options?: HistoryOptions,
  ) => void;
  deleteSelected: () => void;
  removeStaleNodeGroupReference: (staleNodeId: string, groupIndex?: number) => void;
  pruneAllStaleNodeGroupReferences: () => void;
  applyNodeGroups: (groups: NodeGroup[]) => void;
  createNodeGroup: (name: string) => void;
  deleteNodeGroup: (groupId: string) => void;
  enterGroupFocus: (groupId: string) => void;
  exitGroupFocus: () => void;
  createGroupFromSelection: (name?: string) => void;
  addSelectedNodesToGroup: (groupId: string) => void;
  removeSelectedNodesFromGroups: () => void;
  selectGroupMembers: (groupId: string) => void;
  copySelectedNodes: () => void;
  cutSelectedNodes: () => void;
  pasteNodes: () => void;
  snapshotHistory: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  addRoute: (fromId: string, toId: string) => void;
  updateRoute: (id: string, patch: Partial<ProjectRoute>) => void;
  importDocument: (document: ProjectDocument) => void;
  updateSettings: (patch: Partial<ProjectSettings>) => void;
  setLayoutImage: (
    imageId: string,
    width?: number,
    height?: number,
  ) => void;
  removeLayoutImage: () => void;
  startCalibration: (prefill?: CalibrationData) => void;
  cancelCalibration: () => void;
  addCalibrationPoint: (x: number, y: number) => void;
  setCalibrationDistanceInput: (value: string) => void;
  applyCalibration: () => void;
  setProjectName: (name: string) => void;
  patchRecordMeta: (patch: { name?: string; description?: string }) => void;
}

function markDirty(
  document: ProjectDocument,
  state: Pick<EditorState, 'record'>,
): Partial<EditorState> {
  return {
    document,
    isDirty: state.record ? true : false,
  };
}

function historySnapshot(state: EditorState): Partial<EditorState> {
  if (!state.document) return {};
  return {
    historyPast: pushDocumentSnapshot(state.historyPast, state.document),
    historyFuture: [],
  };
}

function markDirtyWithHistory(
  document: ProjectDocument,
  state: EditorState,
  recordHistory = true,
  syncRoutes = false,
): Partial<EditorState> {
  const prepared = syncRoutes ? syncRouteLengthsInDocument(document) : document;
  return {
    ...(recordHistory ? historySnapshot(state) : {}),
    ...markDirty(prepared, state),
  };
}

function pruneSelectionAfterDocumentChange(
  state: EditorState,
  document: ProjectDocument,
): Partial<EditorState> {
  const nodeIds = new Set(document.nodes.map((n) => n.id));
  const routeIds = new Set(document.routes.map((r) => r.id));
  const selectedNodeIds = state.selectedNodeIds.filter((id) => nodeIds.has(id));
  const selectedRouteIds = state.selectedRouteIds.filter((id) => routeIds.has(id));
  const editingNodeId =
    state.editingNodeId && nodeIds.has(state.editingNodeId)
      ? state.editingNodeId
      : null;
  return { selectedNodeIds, selectedRouteIds, editingNodeId };
}

const EMPTY_CALIBRATION_POINTS: [CalibrationPoint | null, CalibrationPoint | null] =
  [null, null];

function uniqueNodeIds(ids: string[]): string[] {
  return [...new Set(ids)];
}

function selectionFocusBump(
  state: Pick<EditorState, 'selectionFocusSeq'>,
  options?: SelectOptions,
): Partial<EditorState> {
  return options?.focus
    ? { selectionFocusSeq: state.selectionFocusSeq + 1 }
    : {};
}

function mergeNodeSelection(
  current: string[],
  ids: string[],
  options?: SelectOptions,
): string[] {
  const next = uniqueNodeIds(ids);
  if (options?.subtractive) {
    const remove = new Set(next);
    return current.filter((id) => !remove.has(id));
  }
  if (options?.additive) {
    return uniqueNodeIds([...current, ...next]);
  }
  return next;
}

const initialState = {
  projectId: null,
  record: null,
  document: null,
  selectedNodeIds: [] as string[],
  selectedRouteIds: [] as string[],
  focusedGroupId: null as string | null,
  selectionFocusSeq: 0,
  nodeClipboard: null as ProjectNode[] | null,
  editingNodeId: null,
  isDirty: false,
  isSaving: false,
  isLoading: false,
  error: null,
  snapToGrid: true,
  showGrid: true,
  routeSourceId: null,
  calibrationMode: false,
  calibrationPoints: EMPTY_CALIBRATION_POINTS,
  calibrationDistanceInput: '',
  permission: 'edit' as ProjectPermissionLevel,
  historyPast: [] as ProjectDocument[],
  historyFuture: [] as ProjectDocument[],
};

export const useEditorStore = create<EditorState>((set, get) => ({
  ...initialState,

  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),

  snapshotHistory: () =>
    set((state) => historySnapshot(state)),

  canUndo: () => get().historyPast.length > 0,
  canRedo: () => get().historyFuture.length > 0,

  undo: () =>
    set((state) => {
      if (!state.document || state.historyPast.length === 0) return state;
      const past = [...state.historyPast];
      const previous = past.pop()!;
      const future = trimHistoryStack([
        cloneDocument(state.document),
        ...state.historyFuture,
      ]);
      return {
        historyPast: past,
        historyFuture: future,
        ...markDirty(previous, state),
        ...pruneSelectionAfterDocumentChange(state, previous),
      };
    }),

  redo: () =>
    set((state) => {
      if (!state.document || state.historyFuture.length === 0) return state;
      const future = [...state.historyFuture];
      const next = future.shift()!;
      const past = trimHistoryStack([
        ...state.historyPast,
        cloneDocument(state.document),
      ]);
      return {
        historyPast: past,
        historyFuture: future,
        ...markDirty(next, state),
        ...pruneSelectionAfterDocumentChange(state, next),
      };
    }),

  loadProject: (record, options) => {
    const document = syncRouteLengthsInDocument(
      normalizeProjectDocument(
        withTypeNodeColors({
          ...record.document,
          name: record.name,
          description: record.description ?? record.document.description,
        }),
      ),
    );
    const previous = options?.preserveUi ? get() : null;
    const nodeIds = new Set(document.nodes.map((node) => node.id));
    const routeIds = new Set(document.routes.map((route) => route.id));
    set({
      projectId: record.id,
      record,
      document,
      permission: record.permission ?? 'edit',
      selectedNodeIds: previous
        ? previous.selectedNodeIds.filter((id) => nodeIds.has(id))
        : [],
      selectedRouteIds: previous
        ? previous.selectedRouteIds.filter((id) => routeIds.has(id))
        : ([] as string[]),
      selectionFocusSeq: previous ? previous.selectionFocusSeq : 0,
      nodeClipboard: previous ? previous.nodeClipboard : null,
      editingNodeId:
        previous?.editingNodeId && nodeIds.has(previous.editingNodeId)
          ? previous.editingNodeId
          : null,
      isDirty: false,
      error: null,
      snapToGrid: record.document.settings?.snap_to_grid ?? true,
      showGrid: true,
      routeSourceId: null,
      calibrationMode: false,
      calibrationPoints: EMPTY_CALIBRATION_POINTS,
      calibrationDistanceInput: '',
      historyPast: [],
      historyFuture: [],
    });
    const scenarios = record.document.scenarios;
    if (scenarios && scenarios.length > 0) {
      useSimulationStore.getState().setScenarios(scenarios);
    }
    void listScenarios(record.id)
      .then((res) => {
        if (res.scenarios.length > 0) {
          useSimulationStore.getState().setScenarios(res.scenarios);
        }
      })
      .catch(() => {
        // scenarios API optional if project not yet saved with scenarios
      });
    void useSimulationStore.getState().hydrateRunPreferences(record.id);
  },

  setDocument: (document) =>
    set((state) => ({
      ...historySnapshot(state),
      document,
      isDirty: state.record ? true : false,
    })),

  selectNode: (id, options) =>
    set((state) => {
      if (id === null) {
        return {
          selectedNodeIds: [],
          selectedRouteIds: [] as string[],
          ...selectionFocusBump(state, options),
        };
      }

      let selectedNodeIds: string[];
      if (options?.subtractive) {
        selectedNodeIds = state.selectedNodeIds.filter((nodeId) => nodeId !== id);
      } else if (options?.additive) {
        selectedNodeIds = state.selectedNodeIds.includes(id)
          ? state.selectedNodeIds
          : [...state.selectedNodeIds, id];
      } else {
        selectedNodeIds = [id];
      }

      return {
        selectedNodeIds,
        selectedRouteIds: [] as string[],
        ...selectionFocusBump(state, options),
      };
    }),

  setNodeSelection: (ids, options) =>
    set((state) => ({
      selectedNodeIds: mergeNodeSelection(state.selectedNodeIds, ids, options),
      selectedRouteIds: [] as string[],
      ...selectionFocusBump(state, options),
    })),

  setMarqueeSelection: (nodeIds, routeIds, options) =>
    set((state) => ({
      selectedNodeIds: mergeNodeSelection(state.selectedNodeIds, nodeIds, options),
      selectedRouteIds: mergeNodeSelection(state.selectedRouteIds, routeIds, options),
      ...selectionFocusBump(state, options),
    })),

  selectRoute: (id, options) =>
    set((state) => {
      if (id === null) {
        return {
          selectedRouteIds: [],
          ...selectionFocusBump(state, options),
        };
      }

      let selectedRouteIds: string[];
      if (options?.subtractive) {
        selectedRouteIds = state.selectedRouteIds.filter((routeId) => routeId !== id);
      } else if (options?.additive) {
        selectedRouteIds = state.selectedRouteIds.includes(id)
          ? state.selectedRouteIds
          : [...state.selectedRouteIds, id];
      } else {
        selectedRouteIds = [id];
      }

      return {
        selectedRouteIds,
        selectedNodeIds: selectedRouteIds.length > 0 ? [] : state.selectedNodeIds,
        ...selectionFocusBump(state, options),
      };
    }),

  focusSelection: () =>
    set((state) => ({
      selectionFocusSeq: state.selectionFocusSeq + 1,
    })),

  openNodeEdit: (id) =>
    set({ editingNodeId: id, selectedNodeIds: [id], selectedRouteIds: [] }),

  closeNodeEdit: () => set({ editingNodeId: null }),

  removeNodeFromDocument: (id) =>
    set((state) => {
      if (!state.document) return state;
      const document = withPrunedNodeGroups(
        {
          ...state.document,
          nodes: state.document.nodes.filter((n) => n.id !== id),
          routes: state.document.routes.filter(
            (r) => r.from_node_id !== id && r.to_node_id !== id,
          ),
        },
        new Set([id]),
      );
      return {
        ...markDirtyWithHistory(document, state),
        selectedNodeIds: [],
        editingNodeId: null,
      };
    }),

  setSaving: (isSaving) => set({ isSaving }),
  markSaved: () => set({ isDirty: false }),

  // --------------------------------------------------------
  // saveProject
  // Persists the local document (including unsaved nodes and
  // routes) and reloads the saved record. Keeps the node
  // dialog open when that node is still in the project.
  // --------------------------------------------------------
  saveProject: async () => {
    const state = get();
    if (
      state.permission !== 'edit' ||
      state.isSaving ||
      !state.projectId ||
      !state.document ||
      !state.record
    ) {
      return false;
    }

    set({ isSaving: true, error: null });
    try {
      const scenarios = useSimulationStore.getState().scenarios;
      const updated = await updateProject(state.projectId, {
        name: state.document.name,
        description: state.document.description,
        document: { ...state.document, scenarios },
      });
      get().loadProject(updated, { preserveUi: true });
      return true;
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to save',
      });
      return false;
    } finally {
      set({ isSaving: false });
    }
  },
  reset: () => {
    useSimulationStore.getState().reset();
    set(initialState);
  },

  toggleSnapToGrid: () =>
    set((state) => {
      if (!state.document) return state;
      const snap = !state.snapToGrid;
      const settings = { ...state.document.settings, snap_to_grid: snap };
      return {
        snapToGrid: snap,
        ...markDirtyWithHistory({ ...state.document, settings }, state),
      };
    }),

  toggleShowGrid: () => set((state) => ({ showGrid: !state.showGrid })),

  setRouteSource: (id) => set({ routeSourceId: id }),

  addNode: (type) =>
    set((state) => {
      if (!state.document) return state;
      const names = state.document.nodes.map((n) => n.name);
      const offset = state.document.nodes.length * 24;
      const appearance = resolveNodeTypeAppearance(type, state.document.settings);
      const node: ProjectNode = {
        id: crypto.randomUUID(),
        type,
        name: defaultNodeName(type, names),
        x: 120 + offset,
        y: 120 + offset,
        color: appearance.color,
        radius: appearance.radius,
        enabled: true,
        display_label: true,
        show_in_simulation_controls: type === 'input' || type === 'gate',
        ...typeSpecificDefaults(type),
      };
      const document = {
        ...state.document,
        nodes: [...state.document.nodes, node],
      };
      return {
        selectedNodeIds: [node.id],
        selectedRouteIds: [] as string[],
        routeSourceId: null,
        ...markDirtyWithHistory(document, state),
      };
    }),

  updateNode: (id, patch) =>
    set((state) => {
      if (!state.document) return state;
      const document = {
        ...state.document,
        nodes: state.document.nodes.map((n) =>
          n.id === id ? { ...n, ...patch } : n,
        ),
      };
      const syncRoutes = 'x' in patch || 'y' in patch;
      return markDirtyWithHistory(document, state, true, syncRoutes);
    }),

  moveNode: (id, x, y, options) =>
    set((state) => {
      if (!state.document) return state;
      const gs = effectiveGridSize(state.document?.settings);
      const snappedX = snapValue(x, gs, state.snapToGrid);
      const snappedY = snapValue(y, gs, state.snapToGrid);
      const document = {
        ...state.document,
        nodes: state.document.nodes.map((n) =>
          n.id === id ? { ...n, x: snappedX, y: snappedY } : n,
        ),
      };
      return markDirtyWithHistory(
        document,
        state,
        options?.recordHistory !== false,
        true,
      );
    }),

  moveSelectedNodesByDelta: (dx, dy, options) =>
    set((state) => {
      if (!state.document || state.selectedNodeIds.length === 0) return state;
      const selected = new Set(state.selectedNodeIds);
      const gs = effectiveGridSize(state.document?.settings);
      const document = {
        ...state.document,
        nodes: state.document.nodes.map((n) => {
          if (!selected.has(n.id)) return n;
          const nextX = n.x + dx;
          const nextY = n.y + dy;
          return {
            ...n,
            x: snapValue(nextX, gs, state.snapToGrid),
            y: snapValue(nextY, gs, state.snapToGrid),
          };
        }),
      };
      return markDirtyWithHistory(
        document,
        state,
        options?.recordHistory !== false,
        true,
      );
    }),

  deleteSelected: () =>
    set((state) => {
      if (!state.document) return state;
      const nodeIds = state.selectedNodeIds;
      const routeIds = state.selectedRouteIds;
      if (nodeIds.length === 0 && routeIds.length === 0) return state;

      let document = state.document;
      if (nodeIds.length > 0) {
        const remove = new Set(nodeIds);
        document = withPrunedNodeGroups(
          {
            ...document,
            nodes: document.nodes.filter((n) => !remove.has(n.id)),
            routes: document.routes.filter(
              (r) => !remove.has(r.from_node_id) && !remove.has(r.to_node_id),
            ),
          },
          remove,
        );
      }

      if (routeIds.length > 0) {
        const removeRoutes = new Set(routeIds);
        document = {
          ...document,
          routes: document.routes.filter((r) => !removeRoutes.has(r.id)),
        };
      }

      return {
        selectedNodeIds: [],
        selectedRouteIds: [] as string[],
        routeSourceId: null,
        editingNodeId: nodeIds.includes(state.editingNodeId ?? '')
          ? null
          : state.editingNodeId,
        ...markDirtyWithHistory(document, state),
      };
    }),

  removeStaleNodeGroupReference: (staleNodeId, groupIndex) =>
    set((state) => {
      if (!state.document) return state;
      const groups = normalizeNodeGroups(state.document);
      if (!groups.length) return state;
      const nextGroups = groups.map((group, index) => {
        if (groupIndex != null && index !== groupIndex) return group;
        if (!group.node_ids.includes(staleNodeId)) return group;
        return {
          ...group,
          node_ids: group.node_ids.filter((id) => id !== staleNodeId),
        };
      });
      const document = {
        ...state.document,
        node_groups: nextGroups,
      };
      return markDirtyWithHistory(document, state);
    }),

  pruneAllStaleNodeGroupReferences: () =>
    set((state) => {
      if (!state.document) return state;
      const validNodeIds = new Set(state.document.nodes.map((node) => node.id));
      const groups = normalizeNodeGroups(state.document);
      if (!groups.length) return state;
      const document = {
        ...state.document,
        node_groups: pruneStaleNodeGroupReferences(groups, validNodeIds),
      };
      return markDirtyWithHistory(document, state);
    }),

  applyNodeGroups: (groups) =>
    set((state) => {
      if (!state.document) return state;
      const document = syncNodeGroupsToDocument(state.document, groups);
      return markDirtyWithHistory(document, state);
    }),

  createNodeGroup: (name) =>
    set((state) => {
      if (!state.document) return state;
      const trimmed = name.trim();
      if (!trimmed) return state;
      const groups = normalizeNodeGroups(state.document);
      if (groups.some((group) => group.id === trimmed || group.name === trimmed)) {
        return state;
      }
      const document = syncNodeGroupsToDocument(state.document, [
        ...groups,
        { id: trimmed, name: trimmed, node_ids: [] },
      ]);
      return markDirtyWithHistory(document, state);
    }),

  deleteNodeGroup: (groupId) =>
    set((state) => {
      if (!state.document) return state;
      const groups = normalizeNodeGroups(state.document).filter(
        (group) => group.id !== groupId,
      );
      const document = syncNodeGroupsToDocument(state.document, groups);
      return {
        ...markDirtyWithHistory(document, state),
        focusedGroupId: state.focusedGroupId === groupId ? null : state.focusedGroupId,
      };
    }),

  enterGroupFocus: (groupId) => set({ focusedGroupId: groupId }),

  exitGroupFocus: () => set({ focusedGroupId: null }),

  createGroupFromSelection: (name) =>
    set((state) => {
      if (!state.document || state.selectedNodeIds.length === 0) return state;
      const groups = normalizeNodeGroups(state.document);
      const selected = new Set(state.selectedNodeIds);
      const groupName =
        name?.trim() ||
        `Group ${groups.filter((g) => g.name.startsWith('Group ')).length + 1}`;
      const newGroup: NodeGroup = {
        id: crypto.randomUUID(),
        name: groupName,
        node_ids: [...state.selectedNodeIds],
      };
      const withoutOverlap = removeNodesFromAllGroups(groups, selected);
      const document = syncNodeGroupsToDocument(state.document, [
        ...withoutOverlap,
        newGroup,
      ]);
      return {
        ...markDirtyWithHistory(document, state),
        focusedGroupId: newGroup.id,
        selectedNodeIds: [...newGroup.node_ids],
      };
    }),

  addSelectedNodesToGroup: (groupId) =>
    set((state) => {
      if (!state.document || state.selectedNodeIds.length === 0) return state;
      const groups = normalizeNodeGroups(state.document);
      const target = groups.find((g) => g.id === groupId);
      if (!target) return state;
      const nextGroups = addNodesToGroup(groups, groupId, state.selectedNodeIds);
      const document = syncNodeGroupsToDocument(state.document, nextGroups);
      const updated = nextGroups.find((g) => g.id === groupId)!;
      const members = [...collectGroupMemberIds(updated, document.nodes)];
      return {
        ...markDirtyWithHistory(document, state),
        selectedNodeIds: members,
        focusedGroupId: state.focusedGroupId ?? groupId,
      };
    }),

  removeSelectedNodesFromGroups: () =>
    set((state) => {
      if (!state.document || state.selectedNodeIds.length === 0) return state;
      const selected = new Set(state.selectedNodeIds);
      const groups = removeNodesFromAllGroups(
        normalizeNodeGroups(state.document),
        selected,
      );
      const document = syncNodeGroupsToDocument(state.document, groups);
      return markDirtyWithHistory(document, state);
    }),

  selectGroupMembers: (groupId) =>
    set((state) => {
      if (!state.document) return state;
      const group = findGroupById(state.document, groupId);
      if (!group) return state;
      const members = [...collectGroupMemberIds(group, state.document.nodes)];
      return {
        selectedNodeIds: members,
        selectedRouteIds: [] as string[],
      };
    }),

  copySelectedNodes: () =>
    set((state) => {
      if (!state.document || state.selectedNodeIds.length === 0) return state;
      const selected = new Set(state.selectedNodeIds);
      const nodes = state.document.nodes.filter((n) => selected.has(n.id));
      return { nodeClipboard: structuredClone(nodes) };
    }),

  cutSelectedNodes: () =>
    set((state) => {
      if (!state.document || state.selectedNodeIds.length === 0) return state;
      const selected = new Set(state.selectedNodeIds);
      const nodeClipboard = structuredClone(
        state.document.nodes.filter((n) => selected.has(n.id)),
      );
      const document = withPrunedNodeGroups(
        {
          ...state.document,
          nodes: state.document.nodes.filter((n) => !selected.has(n.id)),
          routes: state.document.routes.filter(
            (r) => !selected.has(r.from_node_id) && !selected.has(r.to_node_id),
          ),
        },
        selected,
      );
      return {
        nodeClipboard,
        selectedNodeIds: [],
        selectedRouteIds: [] as string[],
        routeSourceId: null,
        editingNodeId: selected.has(state.editingNodeId ?? '')
          ? null
          : state.editingNodeId,
        ...markDirtyWithHistory(document, state),
      };
    }),

  pasteNodes: () =>
    set((state) => {
      if (!state.document || !state.nodeClipboard?.length) return state;
      const offset = 32;
      const names = [...state.document.nodes.map((n) => n.name)];
      const newIds: string[] = [];
      const pasted = state.nodeClipboard.map((node) => {
        const id = crypto.randomUUID();
        newIds.push(id);
        const type = (node.type ?? node.node_type ?? 'waypoint') as NodeType;
        const baseName =
          typeof node.name === 'string' ? node.name : String(node.name ?? 'Node');
        let name = `${baseName} copy`;
        if (names.includes(name)) {
          name = defaultNodeName(type, names);
        }
        names.push(name);
        return {
          ...node,
          id,
          type,
          name,
          x: node.x + offset,
          y: node.y + offset,
        };
      });

      const document = {
        ...state.document,
        nodes: [...state.document.nodes, ...pasted],
      };

      return {
        selectedNodeIds: newIds,
        selectedRouteIds: [] as string[],
        routeSourceId: null,
        ...markDirtyWithHistory(document, state),
      };
    }),

  addRoute: (fromId, toId) =>
    set((state) => {
      if (!state.document || fromId === toId) return state;
      const exists = state.document.routes.some(
        (r) => r.from_node_id === fromId && r.to_node_id === toId,
      );
      if (exists) return { routeSourceId: null };

      const fromNode = state.document.nodes.find((n) => n.id === fromId);
      const toNode = state.document.nodes.find((n) => n.id === toId);
      const routeName =
        fromNode && toNode
          ? `${fromNode.name} → ${toNode.name}`
          : `Route ${state.document.routes.length + 1}`;

      const route: ProjectRoute = {
        id: crypto.randomUUID(),
        name: routeName,
        from_node_id: fromId,
        to_node_id: toId,
        speed_m_per_min:
          state.document.settings?.default_route_speed_m_per_min ?? 60,
        color: state.document.settings?.default_route_color ?? '#878786',
        width: state.document.settings?.default_route_width ?? 6,
      };

      const document = {
        ...state.document,
        routes: [...state.document.routes, route],
      };

      return {
        selectedRouteIds: [route.id],
        selectedNodeIds: [],
        routeSourceId: null,
        ...markDirtyWithHistory(document, state, true, true),
      };
    }),

  updateRoute: (id, patch) =>
    set((state) => {
      if (!state.document) return state;
      const document = {
        ...state.document,
        routes: state.document.routes.map((r) =>
          r.id === id ? { ...r, ...patch } : r,
        ),
      };
      const syncRoutes =
        'waypoints' in patch ||
        'from_node_id' in patch ||
        'to_node_id' in patch;
      return markDirtyWithHistory(document, state, true, syncRoutes);
    }),

  importDocument: (document) => {
    const prepared = syncRouteLengthsInDocument(normalizeProjectDocument(document));
    set((state) => ({
      ...historySnapshot(state),
      document: prepared,
      selectedNodeIds: [],
      selectedRouteIds: [] as string[],
      routeSourceId: null,
      calibrationMode: false,
      calibrationPoints: EMPTY_CALIBRATION_POINTS,
      calibrationDistanceInput: '',
      snapToGrid: prepared.settings?.snap_to_grid ?? true,
      ...markDirty(prepared, state),
    }));
  },

  updateSettings: (patch) =>
    set((state) => {
      if (!state.document) return state;
      const settings = { ...state.document.settings, ...patch };
      const document = { ...state.document, settings };
      return markDirtyWithHistory(document, state);
    }),

  setLayoutImage: (imageId, width, height) =>
    set((state) => {
      if (!state.document) return state;
      const document = {
        ...state.document,
        layout_image_id: imageId,
        layout_image_path: undefined,
        ...(width && height
          ? { layout_width: width, layout_height: height }
          : {}),
      };
      return markDirtyWithHistory(document, state);
    }),

  removeLayoutImage: () =>
    set((state) => {
      if (!state.document) return state;
      const document = {
        ...state.document,
        layout_image_id: undefined,
        layout_image_path: undefined,
        settings: {
          ...state.document.settings,
          calibration_points: undefined,
        },
      };
      return {
        calibrationMode: false,
        calibrationPoints: EMPTY_CALIBRATION_POINTS,
        calibrationDistanceInput: '',
        ...markDirtyWithHistory(document, state),
      };
    }),

  startCalibration: (prefill) =>
    set(() => {
      if (prefill) {
        return {
          calibrationMode: true,
          calibrationPoints: [prefill.p1, prefill.p2] as [
            CalibrationPoint | null,
            CalibrationPoint | null,
          ],
          calibrationDistanceInput: String(prefill.distance),
          routeSourceId: null,
          selectedNodeIds: [],
          selectedRouteIds: [] as string[],
        };
      }
      return {
        calibrationMode: true,
        calibrationPoints: EMPTY_CALIBRATION_POINTS,
        calibrationDistanceInput: '',
        routeSourceId: null,
        selectedNodeIds: [],
        selectedRouteIds: [] as string[],
      };
    }),

  cancelCalibration: () =>
    set({
      calibrationMode: false,
      calibrationPoints: EMPTY_CALIBRATION_POINTS,
      calibrationDistanceInput: '',
    }),

  addCalibrationPoint: (x, y) =>
    set((state) => {
      if (!state.calibrationMode) return state;
      const [p1, p2] = state.calibrationPoints;
      const point: CalibrationPoint = { x, y };
      if (!p1) {
        return {
          calibrationPoints: [point, null] as [
            CalibrationPoint | null,
            CalibrationPoint | null,
          ],
        };
      }
      if (!p2) {
        return {
          calibrationPoints: [p1, point] as [
            CalibrationPoint | null,
            CalibrationPoint | null,
          ],
        };
      }
      return {
        calibrationPoints: [point, null] as [
          CalibrationPoint | null,
          CalibrationPoint | null,
        ],
      };
    }),

  setCalibrationDistanceInput: (value) =>
    set({ calibrationDistanceInput: value }),

  applyCalibration: () =>
    set((state) => {
      if (!state.document) return state;
      const [p1, p2] = state.calibrationPoints;
      if (!p1 || !p2) return state;

      const distance = parseFloat(state.calibrationDistanceInput);
      if (!Number.isFinite(distance) || distance <= 0) return state;

      const unit: UnitSystem =
        state.document.settings?.unit_system ?? 'metric_m';
      const pixelsPerMeter = computePixelsPerMeter(p1, p2, distance, unit);
      if (pixelsPerMeter <= 0) return state;

      const calibration_points: CalibrationData = {
        p1,
        p2,
        distance,
        unit,
      };

      const settings: ProjectSettings = {
        ...state.document.settings,
        pixels_per_meter: pixelsPerMeter,
        calibration_points,
      };

      const document = { ...state.document, settings };
      return {
        calibrationMode: false,
        calibrationPoints: EMPTY_CALIBRATION_POINTS,
        calibrationDistanceInput: '',
        ...markDirtyWithHistory(document, state, true, true),
      };
    }),

  setProjectName: (name) =>
    set((state) => {
      if (!state.document) return state;
      const trimmed = name.trim() || 'Untitled Project';
      const document = { ...state.document, name: trimmed };
      return {
        ...markDirtyWithHistory(document, state),
        record: state.record ? { ...state.record, name: trimmed } : state.record,
      };
    }),

  patchRecordMeta: (patch) =>
    set((state) => {
      if (!state.record) return state;
      const name = patch.name ?? state.record.name;
      const description = patch.description ?? state.record.description;
      const document = state.document
        ? { ...state.document, name, description }
        : state.document;
      return {
        record: { ...state.record, name, description },
        ...(document
          ? markDirtyWithHistory(document, state, Boolean(patch.name))
          : { document }),
      };
    }),
}));

export function getPrimarySelectedNodeId(state: EditorState): string | null {
  return state.selectedNodeIds[0] ?? null;
}

export function isNodeSelected(state: EditorState, nodeId: string): boolean {
  return state.selectedNodeIds.includes(nodeId);
}

export function getSelectedNode(state: EditorState): ProjectNode | null {
  const id = getPrimarySelectedNodeId(state);
  if (!state.document || !id) return null;
  return state.document.nodes.find((n) => n.id === id) ?? null;
}

export function getSelectedNodes(state: EditorState): ProjectNode[] {
  if (!state.document || state.selectedNodeIds.length === 0) return [];
  const selected = new Set(state.selectedNodeIds);
  return state.document.nodes.filter((n) => selected.has(n.id));
}

export function polylineCentroid(points: number[]): { x: number; y: number } | null {
  if (points.length < 2) return null;
  let sumX = 0;
  let sumY = 0;
  let count = 0;
  for (let i = 0; i < points.length; i += 2) {
    sumX += points[i];
    sumY += points[i + 1];
    count += 1;
  }
  if (count === 0) return null;
  return { x: sumX / count, y: sumY / count };
}

export function centerViewportOnPoint(
  x: number,
  y: number,
  viewportWidth: number,
  viewportHeight: number,
  zoom: number,
): { zoom: number; panX: number; panY: number } {
  const clampedZoom = Math.max(0.15, Math.min(10, zoom));
  return {
    zoom: clampedZoom,
    panX: viewportWidth / 2 - x * clampedZoom,
    panY: viewportHeight / 2 - y * clampedZoom,
  };
}

export function fitToContent(
  nodes: ProjectNode[],
  layoutWidth: number,
  layoutHeight: number,
  viewportWidth: number,
  viewportHeight: number,
): { zoom: number; panX: number; panY: number } {
  if (nodes.length === 0) {
    const zoom = Math.min(
      viewportWidth / layoutWidth,
      viewportHeight / layoutHeight,
      1,
    );
    return {
      zoom: Math.max(0.2, zoom * 0.9),
      panX: (viewportWidth - layoutWidth * zoom * 0.9) / 2,
      panY: (viewportHeight - layoutHeight * zoom * 0.9) / 2,
    };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of nodes) {
    const r = n.radius ?? 18;
    minX = Math.min(minX, n.x - r);
    minY = Math.min(minY, n.y - r);
    maxX = Math.max(maxX, n.x + r);
    maxY = Math.max(maxY, n.y + r + 20);
  }

  const padding = 48;
  const contentW = maxX - minX + padding * 2;
  const contentH = maxY - minY + padding * 2;
  const zoom = Math.min(
    viewportWidth / contentW,
    viewportHeight / contentH,
    2,
  );
  const clampedZoom = Math.max(0.15, Math.min(10, zoom * 0.92));
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  return {
    zoom: clampedZoom,
    panX: viewportWidth / 2 - centerX * clampedZoom,
    panY: viewportHeight / 2 - centerY * clampedZoom,
  };
}
